'use client';

import { c as _c } from "react/compiler-runtime";
import { useGetCreatedMarketsAndProposalsField } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import type { Ref, RefCallback } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { IdentityBeam, ThumbWithOrigin } from '@/components/market/identity/identity-marks';
import { ShareButton } from '@/components/market/share-button';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { Skeleton } from '@/components/ui/app-skeleton';
import { Link } from '@/i18n/navigation';
import { useFormattedCurrency } from '@/lib/currency/use-formatted-currency';
import { getAnimatedD6Formatter } from '@/lib/format/animated-d6';
export function CreatedMarketRow(t0) {
  const $ = _c(56);
  const {
    pubkey,
    creatorName,
    backerRewards,
    backerRewardsRef
  } = t0;
  const t = useTranslations("profile.created");
  const marketType = useGetCreatedMarketsAndProposalsField(pubkey, "marketType");
  const title = useGetCreatedMarketsAndProposalsField(pubkey, "title");
  const category = useGetCreatedMarketsAndProposalsField(pubkey, "category");
  const image = useGetCreatedMarketsAndProposalsField(pubkey, "coverImageUrl");
  const nsfw = useGetCreatedMarketsAndProposalsField(pubkey, "coverImageNsfw");
  const volume = useGetCreatedMarketsAndProposalsField(pubkey, "volumeTotal");
  const prizePool = useGetCreatedMarketsAndProposalsField(pubkey, "prizePool");
  if (title === undefined || volume === undefined || prizePool === undefined || backerRewards === undefined) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Skeleton className="h-20 w-full rounded-card" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  const t1 = `/markets/${pubkey}`;
  let t2;
  if ($[1] !== t1 || $[2] !== title) {
    t2 = <Link href={t1} aria-label={title} className="absolute inset-0 rounded-[inherit] focus-visible:outline focus-visible:outline-2 focus-visible:outline-border-primary" />;
    $[1] = t1;
    $[2] = title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = category ?? "";
  const t4 = image ?? null;
  const t5 = nsfw === true;
  let t6;
  if ($[4] !== pubkey || $[5] !== t3 || $[6] !== t4 || $[7] !== t5) {
    t6 = {
      kind: "market",
      pubkey,
      category: t3,
      coverImageUrl: t4,
      nsfw: t5
    };
    $[4] = pubkey;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t6 || $[10] !== title) {
    t7 = <OptimizedImage variant="thumbnail" entity={t6} alt={title} size={40} preview={false} />;
    $[9] = t6;
    $[10] = title;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== marketType || $[13] !== t7) {
    t8 = <ThumbWithOrigin marketType={marketType}>{t7}</ThumbWithOrigin>;
    $[12] = marketType;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== title) {
    t9 = <div className="text-body font-medium text-foreground-primary break-words">{title}</div>;
    $[15] = title;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== category || $[18] !== creatorName) {
    t10 = <div className="text-caption text-foreground-tertiary">{creatorName} · {category}</div>;
    $[17] = category;
    $[18] = creatorName;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t9) {
    t11 = <div className="min-w-0">{t9}{t10}</div>;
    $[20] = t10;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11 || $[24] !== t8) {
    t12 = <div className="flex min-w-0 flex-1 flex-col items-start gap-3 md:flex-row md:items-center md:gap-3.5">{t8}{t11}</div>;
    $[23] = t11;
    $[24] = t8;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t) {
    t13 = t("traded");
    $[26] = t;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t13 || $[29] !== volume) {
    t14 = <Metric label={t13} value={volume} />;
    $[28] = t13;
    $[29] = volume;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t) {
    t15 = t("prizePool");
    $[31] = t;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== prizePool || $[34] !== t15) {
    t16 = <Metric label={t15} value={prizePool} />;
    $[33] = prizePool;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== backerRewards || $[37] !== backerRewardsRef || $[38] !== marketType || $[39] !== t) {
    t17 = marketType !== "featured" ? <Metric label={t("backerRewards")} value={backerRewards} reward={true} ref={backerRewardsRef} /> : null;
    $[36] = backerRewards;
    $[37] = backerRewardsRef;
    $[38] = marketType;
    $[39] = t;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== t14 || $[42] !== t16 || $[43] !== t17) {
    t18 = <dl className="grid w-full grid-cols-2 gap-2 md:flex md:w-auto md:justify-end md:gap-6 md:text-right">{t14}{t16}{t17}</dl>;
    $[41] = t14;
    $[42] = t16;
    $[43] = t17;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== pubkey || $[46] !== title) {
    t19 = <span className="absolute right-3.5 top-3.5 z-10 flex shrink-0 items-center md:static"><ShareButton marketPubkey={pubkey} marketTitle={title} /></span>;
    $[45] = pubkey;
    $[46] = title;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== t12 || $[49] !== t18 || $[50] !== t19 || $[51] !== t2) {
    t20 = <div className="relative flex flex-col items-stretch gap-3 rounded-panel-row border border-border-primary p-3.5 transition-colors hover:bg-background-tertiary focus-visible:outline-2 focus-visible:outline-border-brand md:flex-row md:items-center md:gap-3.5">{t2}{t12}{t18}{t19}</div>;
    $[48] = t12;
    $[49] = t18;
    $[50] = t19;
    $[51] = t2;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] !== marketType || $[54] !== t20) {
    t21 = <IdentityBeam marketType={marketType} radius={12}>{t20}</IdentityBeam>;
    $[53] = marketType;
    $[54] = t20;
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  return t21;
}
function Metric(t0) {
  const $ = _c(17);
  const {
    label,
    value,
    reward: t1,
    ref
  } = t0;
  const reward = t1 === undefined ? false : t1;
  const {
    formatD6
  } = useFormattedCurrency();
  let t2;
  if ($[0] !== formatD6 || $[1] !== value) {
    t2 = getAnimatedD6Formatter(formatD6, value);
    $[0] = formatD6;
    $[1] = value;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const format = t2;
  let t3;
  if ($[3] !== label) {
    t3 = <dt className="text-caption text-foreground-tertiary">{label}</dt>;
    $[3] = label;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = `text-body font-medium tabular-nums ${reward ? "text-feedback-success-accent" : "text-foreground-primary"}`;
  let t5;
  if ($[5] !== value) {
    t5 = BigInt(value);
    $[5] = value;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = Number(t5);
  let t7;
  if ($[7] !== format || $[8] !== t6) {
    t7 = <AnimatedNumber value={t6} format={format} animateOnMount={false} />;
    $[7] = format;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t4 || $[11] !== t7) {
    t8 = <dd className={t4}>{t7}</dd>;
    $[10] = t4;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== ref || $[14] !== t3 || $[15] !== t8) {
    t9 = <div ref={ref} className="bg-background-tertiary/50 p-3 max-md:rounded-panel-row md:bg-transparent md:p-0">{t3}{t8}</div>;
    $[13] = ref;
    $[14] = t3;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  return t9;
}